import { Close } from "@/components/Icons/utility/Close.tsx";
import { AdultAgeSelect } from "@/components/Inputs/AdultAgeSelect/AdultAgeSelect.tsx";
import { setChildChangeSource } from "@/components/Inputs/ChildrenAgeSelect/childChangeSource.ts";
import { ChildrenAgeSelect } from "@/components/Inputs/ChildrenAgeSelect/ChildrenAgeSelect.tsx";
import NumInput from "@/components/Inputs/NumInput/NumInput.tsx";
import { useGetExtrasTranslated } from "@/components/ReservationOptions/hooks/useGetExtrasTranslated.ts";
import { CartAccommodationItemsProps } from "@/components/ReservationSidebar/components/CartAccommodationItemsProps.ts";
import { formatCurrency } from "@/helpers/formatCurrency.ts";
import { handleGTMRemoveAccommodationFromCart } from "@/helpers/gtm/handleGTMRemoveAccommodationFromCart.ts";
import { useMaxAdults } from "@/helpers/hooks/useMaxAdults.ts";
import { RatePlanMerged } from "@/helpers/interfaces/RatePlanMerged.ts";
import { useShadowRoot } from "@/helpers/ShadowRootContext.ts";
import { useGetRGTranslated } from "@/helpers/translation/useGetRGTranslated.ts";
import { useGetRPTranslated } from "@/helpers/translation/useGetRPTranslated.ts";
import { MandatoryExtra, RateSellMode } from "@/helpers/types/types.ts";
import { useIsMobile } from "@/helpers/useIsMobile.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { CartAccommodationItem } from "@/stores/EngineStore/interfaces/CartAccommodationItem.ts";
import { ExtendedOption } from "@/stores/EngineStore/interfaces/ExtendedOption.ts";
import { BedDouble, ChevronDown } from "lucide-react";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const CartAccommodationItems = (props: CartAccommodationItemsProps) => {
  const { t } = useTranslation();
  const { containerEl } = useShadowRoot();
  const isMobile = useIsMobile();
  const getExtraTranslated = useGetExtrasTranslated();
  const { getTranslateRGData } = useGetRGTranslated();
  const { getTranslateRPData } = useGetRPTranslated();
  const maxAdults = useMaxAdults();

  const {
    cartAccommodationItems,
    setCartItemAdults,
    setCartItemChild,
    removeCartItem,
    updateCartItemOptions,
    calculatedCartData,
  } = useEngineStore(
    useShallow((state) => ({
      cartAccommodationItems: state.cartAccommodationItems,
      setCartItemAdults: state.setCartItemAdults,
      setCartItemChild: state.setCartItemChild,
      removeCartItem: state.removeCartItem,
      updateCartItemOptions: state.updateCartItemOptions,
      calculatedCartData: state.calculatedCartData,
    })),
  );

  const handleRemoveCartItem = (CAI: CartAccommodationItem) => {
    if (cartAccommodationItems.length === 1) {
      containerEl.classList.remove("is-cart-drawer-opened");
    }
    removeCartItem(CAI.id);
    handleGTMRemoveAccommodationFromCart(CAI);
  };

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const isAccordionMode = cartAccommodationItems.length >= 2;

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const renderAccordionHeader = (CAI: CartAccommodationItem) => {
    const { name: RGName } = getTranslateRGData(CAI.ratePlan.rateGroup);
    const { name: RPName } = getTranslateRPData(CAI.ratePlan);
    const calculatedAcc = calculatedCartData?.accommodations?.find((acc) => acc.identifier === CAI.id);
    const price = calculatedAcc?.priceBreakdown?.withDiscount || getPerPersonDefaultPrice(CAI);
    const isExpanded = expandedIds.has(CAI.id);

    return (
      <div
        className={`c-sidebar-accordion-header ${isExpanded ? "is-expanded" : ""}`}
        onClick={() => toggleExpanded(CAI.id)}
        role="button"
        aria-expanded={isExpanded}
      >
        <div className="c-sidebar-accordion-header__top-row">
          <span className="c-sidebar-accordion-header__name">{RGName}</span>
          {props.hideRemoveRate && (
            <button
              className="c-clear-button c-sidebar-accordion-header__remove"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveCartItem(CAI);
              }}
            >
              <Close />
            </button>
          )}
        </div>
        <span className="c-sidebar-accordion-header__rate-tag">{RPName}</span>
        <div className="c-sidebar-accordion-header__bottom-row">
          <span className="c-sidebar-accordion-header__summary">
            {CAI.adults.length} {t("adults")}
            {CAI.children.length > 0 && (
              <>
                {" "}
                · {CAI.children.length} {CAI.children.length === 1 ? t("child") : t("children")}
              </>
            )}
          </span>
          <div className="c-sidebar-accordion-header__right">
            <span className="c-sidebar-accordion-header__price">{formatCurrency(price)}</span>
            <ChevronDown size={16} className="c-sidebar-accordion-header__chevron" />
          </div>
        </div>
      </div>
    );
  };

  const guestTotalCapacity = (RP: RatePlanMerged): number => {
    if (RP.rateGroup?.guestCapacity && RP.rateGroup?.guestAdditionalCapacity) {
      return RP.rateGroup?.guestCapacity + RP.rateGroup?.guestAdditionalCapacity;
    }
    return RP.rateGroup.guestCapacity;
  };

  const getPerPersonDefaultPrice = (CAI: CartAccommodationItem): number => {
    const rp = CAI.ratePlan;
    const people = CAI.adults.length + CAI.children.length;

    if (rp.rateSellMode === RateSellMode.PerPerson) {
      return (
        rp.childRatePlans?.find((crp) => crp.occupancy === people)?.defaultPriceBreakdown?.withDiscount ||
        rp.defaultPriceBreakdown?.withDiscount
      );
    }
    return rp.defaultPriceBreakdown?.withDiscount;
  };

  const renderCartAccommodationItem = (CAI: CartAccommodationItem, hideHeader = false) => {
    const { name: RGName } = getTranslateRGData(CAI.ratePlan.rateGroup);
    const { name: RPName } = getTranslateRPData(CAI.ratePlan);

    const calculatedAcc = calculatedCartData?.accommodations?.find((acc) => acc.identifier === CAI.id);
    const basePrice = calculatedAcc?.rateBasePrice || getPerPersonDefaultPrice(CAI);

    if (CAI) {
      return (
        <>
          <div className="rateWrap c-sidebar-rate-wrapper">
            <div className="c-sidebar-rate">
              {!hideHeader && (
                <div className="c-sidebar-rate__top">
                  <div className="c-sidebar-rate__header">
                    <div>
                      <h4 className="rateName c-sidebar-rate__title u-h6 u-fw-600">{RGName}</h4>
                      <span className="c-sidebar-accordion-header__rate-tag">{RPName}</span>
                    </div>
                    <div className="c-sidebar-rate__details">
                      <div className="u-h6 u-fw-600 c-sidebar-rate__details-price">
                        {formatCurrency(basePrice)}
                      </div>
                    </div>
                  </div>
                  {props.hideRemoveRate ? (
                    <div className="c-sidebar-rate__details-clear">
                      <button className="c-clear-button" onClick={() => handleRemoveCartItem(CAI)}>
                        <Close />
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
              <div className="c-sidebar-rate__bottom">
                <div className="c-sidebar-rate__quantities">
                  <NumInput
                    label={t("adults")}
                    onChange={(num) => setCartItemAdults(num, CAI.id)}
                    value={CAI.adults.length}
                    decrementDisabled={CAI.adults.length <= 1}
                    incrementDisabled={
                      CAI.adults.length + CAI.children.length >= guestTotalCapacity(CAI.ratePlan) ||
                      CAI.adults.length >= (maxAdults ?? Infinity)
                    }
                    disabled={props.isReserved}
                    min={1}
                    max={Math.min(
                      guestTotalCapacity(CAI.ratePlan) - CAI.children.length,
                      maxAdults ?? Infinity,
                    )}
                  />
                  <NumInput
                    label={t("children")}
                    onChange={(num) => {
                      setChildChangeSource("sidebar");
                      setCartItemChild(num, CAI.id);
                    }}
                    value={CAI.children.length}
                    incrementDisabled={
                      CAI.adults.length + CAI.children.length >= guestTotalCapacity(CAI.ratePlan)
                    }
                    disabled={props.isReserved}
                    max={guestTotalCapacity(CAI.ratePlan) - CAI.adults.length}
                  />
                </div>
              </div>
            </div>
            {CAI.ratePlan.rateGroup.isParcel && <AdultAgeSelect cartItem={CAI} />}
            <ChildrenAgeSelect cartItem={CAI} compact={isMobile} autoOpen instanceId="sidebar" />
          </div>
        </>
      );
    }
  };

  const renderBaseAccommodationLine = (CAI: CartAccommodationItem) => {
    const calculatedAcc = calculatedCartData?.accommodations?.find((acc) => acc.identifier === CAI.id);
    const basePrice = calculatedAcc?.rateBasePrice || getPerPersonDefaultPrice(CAI);

    return (
      <div className="c-sidebar-option-wrapper">
        <div className="option c-sidebar-option">
          <div className="c-sidebar-option__details">
            <div className="c-sidebar-option__details-icon">
              <BedDouble size={12} />
            </div>
            <p className="rateName c-sidebar-option__details-title u-p0">{t("accommodation")}</p>
            <div className="c-sidebar-option__details-price u-h6 u-fw-600">{formatCurrency(basePrice)}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderOptionalExtras = (options: ExtendedOption[], cartItemId: string) => {
    const calculatedAccExtras =
      calculatedCartData?.accommodations?.find((acc) => acc.identifier === cartItemId)?.extras || [];

    if (options.length) {
      return (
        <div className="optionsWrap c-sidebar-option-wrapper">
          {options?.map((option) => {
            const calculatedExtra = calculatedAccExtras?.find((extra) => extra.id === option.id);
            const displayQuantity =
              calculatedExtra && calculatedExtra.calculatedQuantity >= 1
                ? calculatedExtra.calculatedQuantity
                : option.value >= 1
                ? option.value
                : null;
            const totalExtraPrice =
              typeof calculatedExtra?.total === "number"
                ? calculatedExtra?.total
                : option.price * option.value;
            const { name } = getExtraTranslated(option);

            return (
              <div key={option.id} className="option c-sidebar-option">
                <div className="c-sidebar-option__details">
                  {displayQuantity != null && (
                    <span className="c-sidebar-option__details-counter">{displayQuantity}</span>
                  )}
                  <p className="rateName c-sidebar-option__details-title u-p0">{name}</p>
                  <div className="c-sidebar-option__details-price u-h6 u-fw-600">
                    {formatCurrency(totalExtraPrice)}
                  </div>
                  <div className="c-sidebar-option__details-clear">
                    <button
                      className="c-clear-button"
                      onClick={() => updateCartItemOptions({ ...option, value: 0 }, cartItemId)}
                    >
                      <Close />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  };

  const renderMandatoryExtras = (mandatoryExtras: MandatoryExtra[], cartItemId: string) => {
    const calculatedAccExtras =
      calculatedCartData?.accommodations?.find((acc) => acc.identifier === cartItemId)?.extras || [];
    const cartItemAcc = cartAccommodationItems.find((CAI) => CAI.id === cartItemId);

    const calculatedMandatoryExtras = calculatedAccExtras.filter((CAOE) => {
      return cartItemAcc?.mandatoryExtras.some((CE) => CE.id === CAOE.id);
    });

    // Only show mandatory extras that have a matching calculated entry —
    // extras without calculated data are not applicable for the current occupancy
    const activeMandatoryExtras = mandatoryExtras.filter((extra) =>
      calculatedMandatoryExtras.some((CE) => CE.id === extra.id),
    );

    if (activeMandatoryExtras.length) {
      return (
        <div className="c-sidebar-mandatory-options">
          {activeMandatoryExtras.map((extra) => {
            const calculatedExtra = calculatedMandatoryExtras.find((CE) => CE.id === extra.id);
            const displayQuantity =
              calculatedExtra?.calculatedQuantity && calculatedExtra.calculatedQuantity >= 1
                ? calculatedExtra.calculatedQuantity
                : 1;
            const total = calculatedExtra?.total;
            const { name } = getExtraTranslated(extra);

            return (
              <div key={extra.id} className="option c-sidebar-option">
                <div className="c-sidebar-option__details">
                  <span className="c-sidebar-option__details-counter">{displayQuantity}</span>
                  <p className="rateName c-sidebar-option__details-title u-p0">{name}</p>
                  {typeof total === "number" && (
                    <div className="c-sidebar-option__details-price u-h6 u-fw-600">
                      {formatCurrency(total)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  };

  if (!cartAccommodationItems.length) {
    return (
      <div className="c-reservation-sidebar__blank-state">
        <p className="p-1">{t("noAccommodationSelected")}</p>
      </div>
    );
  }
  return cartAccommodationItems?.map((cartAccommodationItem) => {
    const isExpanded = expandedIds.has(cartAccommodationItem.id);
    const bodyClass = isAccordionMode ? (isExpanded ? "is-expanded" : "is-collapsed") : "is-flat";

    return (
      <Fragment key={cartAccommodationItem.id}>
        <div
          className={`c-reservation-sidebar__info ${
            isAccordionMode ? "c-reservation-sidebar__info--accordion" : ""
          }`}
        >
          {isAccordionMode && renderAccordionHeader(cartAccommodationItem)}
          <div className={`c-sidebar-accordion-body ${bodyClass}`}>
            <div className="c-sidebar-accordion-body__inner">
              {renderCartAccommodationItem(cartAccommodationItem, isAccordionMode)}
              {isAccordionMode && renderBaseAccommodationLine(cartAccommodationItem)}
              {renderMandatoryExtras(cartAccommodationItem.mandatoryExtras, cartAccommodationItem.id)}
              {renderOptionalExtras(cartAccommodationItem.optionalExtras, cartAccommodationItem.id)}
            </div>
          </div>
        </div>
      </Fragment>
    );
  });
};
